import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

const offsetToMinutes = (offset) => {
  const match = String(offset || "").trim().match(/^([+-])(\d{1,2}):?(\d{2})$/)
  if (!match) return null
  const sign = match[1] === "-" ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3]))
}

const dateValue = (date) =>
  date && typeof date === "object" && !(date instanceof Date) && date.DateTime
    ? date.DateTime
    : date

export const renderDate = (date, format) => {
  if (!date) return "N/A"
  const parsed = dayjs(dateValue(date))
  return parsed.isValid()
    ? parsed.format(format || "DD MMM YY, h:mm A")
    : "Invalid Date"
}

export const renderDeviceDate = (date, timezone, format) => {
  if (!date) return "N/A"
  const parsed = dayjs(dateValue(date))
  if (!parsed.isValid()) return "Invalid Date"
  const minutes = offsetToMinutes(timezone?.offset)
  const shown = minutes == null ? parsed : parsed.utcOffset(minutes)
  return shown.format(format || "DD MMM YY, h:mm A")
}

/** Compact age like "6h 49m" / "7d 6h 49m" for uptime-style comparisons. */
export const formatDurationSince = (date) => {
  if (!date) return null
  const parsed = dayjs(dateValue(date))
  if (!parsed.isValid()) return null
  const totalMinutes = Math.max(0, dayjs().diff(parsed, "minute"))
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export const renderDeviceDateWithAge = (date, timezone, { prefix } = {}) => {
  const absolute = renderDeviceDate(date, timezone)
  if (absolute === "N/A" || absolute === "Invalid Date") return absolute
  const age = formatDurationSince(date)
  if (!age) return absolute
  const label = prefix ? `${prefix} ${age}` : `${age} ago`
  return `${absolute} · ${label}`
}

export const formatDeviceLocation = (device) => {
  const place = String(device?.location || "").trim()
  if (!place) return ""
  const source = String(device?.locationSource || "").toLowerCase()
  const meters = Number(device?.locationAccuracyMeters)
  const accuracy =
    Number.isFinite(meters) && meters > 0
      ? meters >= 1000
        ? ` ±${Math.round(meters / 1000)} km`
        : ` ±${Math.round(meters)} m`
      : ""
  if (source === "wifi") return `${place} · Wi‑Fi${accuracy}`
  if (source === "vpn") return `${place} · VPN egress (approx.)`
  if (source === "ip" && device?.wifiFingerprint) {
    return `${place} · Public IP (approx.; Wi‑Fi seen)`
  }
  if (source === "ip") return `${place} · Public IP (approx.)`
  return place
}

export const formatAgentVersion = (version) => {
  const value = String(version || "").trim()
  if (!value) return ""
  return value.replace(/^full-/i, "")
}

export const calculateDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "N/A"

  const start = dayjs(fromDate).startOf("day")
  const end = dayjs(toDate).startOf("day")

  if (!start.isValid() || !end.isValid()) return "Invalid Date"

  return end.diff(start, "day") + 1
}
